/**
 * Hook for starting unstake (sUSDv to cooldown queue)
 */
import type { TransactionObserver } from "@shared/types/transactionObserver";
import { type TransactionFlowConfig, type TransactionState, useTransactionFlow } from "@solomon-apps/shared";
import { DEFAULT_SALT, TOKEN_DECIMALS } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { SolomonStake } from "@solomon-labs/solomon-protocol";
import { useMemo } from "react";

/**
 * Return type for useStartUnstakeTransaction hook
 */
export interface UseStartUnstakeTransactionReturn {
    /** Current transaction state */
    state: TransactionState;
    /** Execute start unstake transaction */
    startUnstake: (sharesAmount: BigDecimal) => Promise<string | null>;
    /** Reset state to initial */
    reset: () => void;
}

const INVALIDATE_KEYS = [["user-staking-stats"], ["staking-stats"], ["protocol-data"], ["transactionHistory"]];

/**
 * Hook for starting unstake (burn sUSDv, enter cooldown queue)
 *
 * This burns the user's sUSDv and adds an entry to their unstaking queue.
 * The USDv will be claimable after the cooldown period (7 days).
 *
 * @param salt - Vault salt (default: main vault)
 * @returns Transaction state and execute function
 */
export function useStartUnstakeTransaction(
    salt: Uint8Array = DEFAULT_SALT,
    observer?: TransactionObserver
): UseStartUnstakeTransactionReturn {
    const config = useMemo(
        (): TransactionFlowConfig<[BigDecimal]> => ({
            name: "useStartUnstakeTransaction",
            observer,
            buildTransaction: async (
                [sharesAmount],
                { publicKey, recentBlockhash }
            ): Promise<{
                serialize(): Uint8Array;
            }> => {
                const rawShares = sharesAmount.multiply(BigDecimal.pow10(TOKEN_DECIMALS.sUSDv)).toBigint();
                return SolomonStake.createStartUnstakeTransaction(publicKey, salt, rawShares, {
                    feePayer: publicKey,
                    recentBlockhash
                });
            },
            invalidateQueryKeys: INVALIDATE_KEYS
        }),
        [salt, observer]
    );

    const { state, execute, reset } = useTransactionFlow(config);

    return { state, startUnstake: execute, reset };
}
